import { useState, useEffect, useCallback } from 'react'
import { postFavorites, removeFavorite } from '../../../assets/requests'
import { FAVORITE_CATEGORY_ID } from '../../../constants/categories'
import { useUserInfo } from '../../Authentication/hooks'
import { toast } from 'react-toastify'

/**
 * Shared hook for checking and toggling the favorite state of an asset.
 *
 * @param {Object} options
 * @param {string|null} options.assetId - The current asset ID
 * @param {Object} options.publishedAssets - Published assets map (object keyed by ID)
 * @param {Array|null} options.assetsArray - Assets array (used by category pages)
 * @param {Function} options.dispatchUpdate - Dispatch to update published assets after toggle
 */
export function useFavoriteToggle({ assetId, publishedAssets, assetsArray, dispatchUpdate }) {
  const { userId } = useUserInfo()
  const [isFavorited, setIsFavorited] = useState(false)

  useEffect(() => {
    if (!assetId) return

    // Check in array first (category pages like favorites/duplicates)
    if (Array.isArray(assetsArray)) {
      const asset = assetsArray.find(a => a.id === assetId)
      if (asset) {
        const cats = asset.categories || asset.metadata?.categories || []
        setIsFavorited(cats.some(c => (c.id || c._id) === FAVORITE_CATEGORY_ID))
        return
      }
    }

    // Check in publishedAssets map (gallery page)
    const asset = publishedAssets?.[assetId]
    if (asset) {
      const cats = asset.metadata?.categories || asset.categories || []
      setIsFavorited(cats.some(c => (c.id || c._id) === FAVORITE_CATEGORY_ID))
    }
  }, [assetId, publishedAssets, assetsArray])

  const toggleFavorite = useCallback(async () => {
    if (!assetId) return

    const favPayload = {
      categories: [{ name: 'favorite', type: 'tag', id: FAVORITE_CATEGORY_ID }],
      assetIds: [assetId]
    }

    try {
      if (isFavorited) {
        await removeFavorite({ userId, payload: favPayload })
        setIsFavorited(false)
        toast.success('Removed from favorites', { autoClose: 1000, hideProgressBar: true })
      } else {
        await postFavorites({ userId, payload: favPayload })
        setIsFavorited(true)
        toast.success('Marked as favorite!', { autoClose: 1000, hideProgressBar: true })
      }

      // Update local published assets so thumbnail fav-star updates
      if (dispatchUpdate && publishedAssets?.[assetId]) {
        const updated = { ...publishedAssets }
        const asset = { ...updated[assetId] }
        const cats = [...(asset.metadata?.categories || [])]
        if (isFavorited) {
          asset.metadata = { ...asset.metadata, categories: cats.filter(c => (c.id || c._id) !== FAVORITE_CATEGORY_ID) }
        } else {
          cats.push({ name: 'favorite', type: 'tag', id: FAVORITE_CATEGORY_ID })
          asset.metadata = { ...asset.metadata, categories: cats }
        }
        updated[assetId] = asset
        dispatchUpdate({ type: 'set_published_assets', payload: updated })
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
      const action = isFavorited ? 'unfavorite' : 'favorite'
      toast.error(`Failed to ${action}. ${error?.message || ''}`, { autoClose: 3000, hideProgressBar: true })
    }
  }, [assetId, isFavorited, userId, publishedAssets, dispatchUpdate])

  return { isFavorited, toggleFavorite }
}
